// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

// SyncFusion
import { Column, GridComponent, ISelectedCell, SelectionSettingsModel } from '@syncfusion/ej2-angular-grids';
import { MenuItem } from '@syncfusion/ej2-angular-navigations';
import { Browser } from '@syncfusion/ej2-base';

// Data
import { CerDataService } from '@data/cer-data.service';

// Ui
import { UiActionTypeEnum, UiCommandEvent } from '@ui/cer-ui-types';
import { uiCmdClipboardCopyField, uiCmdClipboardCopyRow, uiCmdClipboardCopyRowWithHeader } from '@ui/cer-ui-commands';

// Grid
import { CerGridCommandService } from './cer-grid-command.service';
import { CerGridSelectionState, CerListSelectionResult } from './cer-grid-selection-state';
import { CerGridFilterService } from './cer-grid-filter.service';
import { GridDesign } from './cer-grid-design';
import { CerGridComponent } from './cer-grid.component';

@Injectable()
export class CerGridSelectionService implements OnDestroy {
  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Grid edit
  public grid: GridComponent;
  public design: GridDesign;
  private appGrid: CerGridComponent

  // Selection
  private selectionInitialFirst: boolean = true;
  private selectionInitialFirstNoFocus: boolean = true;
  private selectionInitialForceParentData: boolean = true;

  // Constructor
  constructor(private data: CerDataService,
    private command: CerGridCommandService) {
    this.subscriptionManager.add(command.commandPre$.subscribe(cmd => this.onCommandPre(cmd)));
    this.subscriptionManager.add(command.commandPost$.subscribe(cmd => this.onCommandPost(cmd)));
  }

  // Subscriptions
  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  private selectionState: CerGridSelectionState;
  public init(appGrid: CerGridComponent, grid: GridComponent, design: GridDesign) {
    this.selectionState = new CerGridSelectionState(grid);
    this.grid = grid;
    this.appGrid = appGrid;
    this.design = design;

    this.initGridSelectionSettings();
  }

  public initGridSelectionSettings() {
    var s: SelectionSettingsModel = {};

    //s.checkboxMode = 'ResetOnRowClick';
    //s.checkboxOnly = true;
    //s.enableSimpleMultiRowSelection = true;

    this.selectionInitialFirst = (['first', 'first-no-focus'].includes(this.design.selectionInitial));
    this.selectionInitialFirstNoFocus = (['first-no-focus'].includes(this.design.selectionInitial));
    switch (this.design.selectionMode) {
      case 'row':
        s.mode = 'Row';
        s.type = 'Single'
        break;
      case 'checkboxMulti':
        s.mode = 'Row';
        s.type = 'Multiple';
        break;
      case 'checkboxSingle':
        s.mode = 'Row';
        s.type = 'Single';
        //s.checkboxOnly = true;
        break;
      case 'multiple':
        s.mode = 'Row';
        s.type = 'Multiple';
        break;
      case 'cellBox':
        s.mode = 'Cell';
        s.type = 'Multiple';
        s.cellSelectionMode = 'Box';
        break;
    }
    this.grid.selectionSettings = s;
    this.grid.selectionSettings.type = s.type;  // Change detect trigger?
  }

  public isSelectionModeCell(): boolean {
    return this.grid?.selectionSettings?.mode == 'Cell';
  }

  public onCommandPre(event: UiCommandEvent) {
    switch (event.source) {
      case 'actionBegin':
        switch (event.actionType) {
          case 'beginEdit':
            this.rowSelectedSave(event.args.index);
            break;
          case 'save':
            this.rowSelectedSave(event.args.index);
            break;
          case 'delete':
            this.rowSelectedSave();
            break;
          case 'rowSelecting':
            this.onRowSelectingPre(event.args);
            break;
          case 'rowSelect':
            this.onRowSelectedPre(event.args);
            break;
          case 'rowDeselect':
            //this.onRowDeselectBegin(cmd.args);
            break;
        }
        break;
      case 'contextMenu':
        switch (event.actionType) {
          case 'contextMenuOpen':
            this.onContextMenuOpen(event);
            break;
        }
        break;
    }
  }

  public onCommandPost(event: UiCommandEvent) {
    if (event.source == 'actionBegin') {
      switch (event.actionType) {
        case 'sorting':
        case 'filtering':
          this.selectionState.clear();
          this.grid.pageSettings.currentPage = 1;
          break;
      }
    }
    if (event.source == 'actionComplete') {
      switch (event.actionType) {
        case 'dataBound':
          this.rowSelectStateOrFirst(event.rowsData);
          break;
        case 'refresh':
          if (this.design?.autoFocus) {
            this.rowSelectedRestore();
          }
          break;
        case 'save':
          if (event.args.action == "edit" && this.grid.editSettings.mode == "Dialog") {
            this.grid.selectRow(event.args.rowIndex);
            //this.rowSelectedKeep(args.rowIndex);
            //this.rowSelectedKeepFocus = true;
            //this.grid.focusModule.focusContent();
          }
          else {
            //    this.rowSelectKeepIdx();
          }
          break;

      }
    }
    switch (event.commandId) {
      case uiCmdClipboardCopyField.id:
        this.doCopySelectedField(event);
        break;
      case uiCmdClipboardCopyRow.id:
        this.grid.copy();
        break;
      case uiCmdClipboardCopyRowWithHeader.id:
        this.grid.copy(true);
        break;
    }
  }

  private onRowSelectingPre(args: any) {
    if (this.design?.selectionMode == 'checkboxSingle') { //
      if (this.grid.getSelectedRecords().length) {
        this.grid.clearSelection();
      }
    }
  }

  public onRowSelectedPre(args: any) {
    this.nextSelectionIsInternal(); // Make sure next selection can focus
    /*if (this.grid.isEdit) {
      this.rowSelectedKeep(args.index);
      args.cancel = true;
      this.grid.endEdit();
    }
    else {*/

    //if (this.rowSelectedKeepFocus) {
    //  this.rowSelectedKeepFocus = false;
    //  var idx = args.rowIndex;
    //  if (idx) {
    //  this.grid.clearSelection();
    //this.grid.element.focus();
    //  this.grid.focusModule.focusContent();
    //  this.grid.selectRow(idx);
    //  }
    //}
  }

  // Row selection
  public nextSelectionIsExtenal() {
    if (this.design?.autoFocus && this.selectionInitialFirstNoFocus) {
      // Make sure intial selection from parent will not change focus to selection
      if (this.grid.selectionModule) {
        this.grid.selectionModule.preventFocus = true; // Next selection should not focus
      }
    }
  }

  public nextSelectionIsInternal() {
    if (this.design?.autoFocus && this.selectionInitialFirstNoFocus) {
      this.grid.selectionModule.preventFocus = false; // Further selections should focus
    }
  }

  // Set Selection
  private rowLastIdx(): number {
    var rows = this.grid.getRows()
    return rows ? rows.length - 1 : null; // Last visible row index
  }

  public rowSelectFirst(keep: boolean = false): boolean {
    if (this.isSelectionModeCell()) {
      const col = /*this.editableColumnFirst() ??*/ this.visibleColumnFirst();
      if (col?.index != null) {
        var index: number = this.columnsGet().findIndex(function (c: Column) { return c === col });
        if (index >= 0) {
          this.cellSelectByIdx(0, index);
        }
      }
      return true;
    }
    else {
      return this.rowSelectByIdx(0);
    }
  }

  private columnsGet(): Column[] {
    return this.grid.columns as Column[];
  }

  private visibleColumnsGet(): Column[] {
    return this.grid.getVisibleColumns();
  }

  private editableColumnsGet(): Column[] {
    return this.grid.getVisibleColumns().filter(c => c.allowEditing == true);
  }

  private editableColumnFirst(): Column {
    if (!this.appGrid.isEditable()) {
      return null;
    }
    const cols = this.editableColumnsGet();
    return cols?.length > 0 ? cols[0] : null;
  }

  private visibleColumnFirst(): Column {
    const cols = this.visibleColumnsGet();
    return cols?.length > 0 ? cols[0] : null;
  }

  public cellSelected(): ISelectedCell {
    //getSelectedCellsElement
    const cells: ISelectedCell[] = this.cellsSelected();
    return cells?.length > 0 ? cells[0] : null;
  }

  public cellsSelected(): ISelectedCell[] {
    //getSelectedCellsElement
    return this.grid.getSelectedRowCellIndexes();
  }

  public cellSelectByIdx(rowIndex: number, cellIndex: number) {
    const rows = this.grid.getDataRows().length;
    if (rows <= rowIndex) {
      rowIndex = rows - 1;
    }
    if (rowIndex >= 0 && cellIndex >= 0) {
      this.grid.selectCell({ rowIndex: rowIndex, cellIndex: cellIndex }, false);
    }
  }

  private rowSelectToggleByIdx(idx: number) {
    this.grid.selectRow(idx, true);
  }

  public rowSelectByIdx(idx: number, keep: boolean = false): boolean {
    var ok: boolean = (keep && this.rowSelectedRowIdx != null);  // Keep existing selection

    if (!ok) {
      var selectIdx = idx <= this.rowLastIdx() ? idx : this.rowLastIdx();  // Re-select idx or last idx visible list shorter
      if (selectIdx >= 0) {
        this.grid.selectRow(selectIdx);
      }
      ok = true;
    }
    return ok;
  }

  /*
  public rowSelectByData(rowData: any, keep: boolean = false): boolean {
    var ok: boolean = (keep && this.rowSelectedIdx != null);  // Keep existing selection

    if (!ok) {
      var selectIdx = this.rowDataById(rowSel );  // Re-select idx or last idx visible list shorter
      if (selectIdx >= 0) {
        this.grid.selectRow(selectIdx);
      }
      ok = true;
    }
    return ok;
  }
  */

  public rowSelectRefresh(research: boolean = false): boolean {
    var selectedIdx = this.rowSelectedRowIdx();
    if (selectedIdx) {
      if (research) {
        // TODO: refresh this record ?
      }
      this.rowSelectByIdx(selectedIdx);
    }

    return (selectedIdx != null);
  }

  private rowSelectStateOrFirst(rowsData: any[]) {
    if (this.selectionInitialFirst) {
      if (this.rowSelectedRowIdx() == null) {
        const res = this.rowSelectedRestore();
        if (res != 'pageSelected') {
          //this.selectionState.clear();
        }
        if (res == 'noSelection' && this.design.autoFocus) {
          if (this.selectionState.isEmpty()) {
            this.grid.scrollModule.refresh();
            this.rowSelectFirst();
          }
        }
        else if (this.selectionInitialForceParentData) {
          if ( rowsData?.length > 0) {
            this.appGrid.rowSelectVirtual(0,rowsData[0]);
          }
        }
      }
    }
    else {
      this.rowSelectedClear();
    }
  }

  private rowSelectClearExceptIdx(idx: number) {
    var rows = this.rowsSelectedDataIdx();
    rows.forEach(rowIdx => { if (rowIdx != idx) { this.rowSelectToggleByIdx(rowIdx) } });
  }

  // Get Selection
  public rowSelectedActive(): boolean {
    return (this.grid.getSelectedRecords().length > 0);
  }

  public rowsSelectedDataIdx(): number[] {
    return this.grid.getSelectedRowIndexes();
  }

  public rowSelectedRowIdx(): number {
    return this.selectionState.rowSelectedRowIdxGet();
  }

  public cellsSelectedValues(): any[] {
    var cellValues: any[] = [];
    var rowCellValues: ISelectedCell[] = this.grid.getSelectedRowCellIndexes();
    if (rowCellValues?.length > 0) {
      rowCellValues?.forEach(c => {
        // TODO: implement logic to get cell values
      });
    }
    else {
      var colIds: string[] = this.grid.getSelectedColumnsUid();
      if (colIds?.length > 0) {
        colIds?.forEach(c => {
          console.log(c);
          // TODO: implement logic to get cell values
        })
      }
      else {
        var value: any = (<any>this.grid.selectionModule).actualTarget;
        if (value != null) {
          cellValues.push(value.innerText);
        }
      }
    }
    return cellValues;
  }

  public rowsSelectedData(): any[] {
    if (this.isSelectionModeCell()) {
      const cells = (<any>this.grid.selectionModule).getSelectedColumnCells(null);
      return [];
    }
    else {
      return this.grid.getSelectedRecords();
    }
  }

  public rowsSelectedId(): number[] {
    var idList: number[] = [];
    this.rowsSelectedData().forEach(row => idList.push((<any>row).id));
    return idList;
  }

  public rowSelectedData(): any {
    var selectedRow1: any = null;
    var selectedRows: any[] = this.rowsSelectedData();
    if (selectedRows.length >= 1) {
      selectedRow1 = selectedRows[0];
    }
    return selectedRow1;
  }

  //public rowSelectedDataKeep() {
  //  this.rowSelectedKeepData = this.rowSelectedData();
  //}

  public rowSelectedSaveFirst() {
    this.selectionState.saveFirstRowOnFirstPage();
  }

  public rowSelectedSave(idx: number = null, relativeRowIdx: number = null): boolean {
    return this.selectionState?.save(idx, relativeRowIdx);
  }

  public rowSelectedRestore(pageSize: number = null): CerListSelectionResult {
    return this.selectionState?.restore(pageSize);
  }

  public rowSelectedClear() {
    this.grid.clearSelection();
    this.selectionState.clear();
  }

  public rowsCanSelectMulti(): boolean {
    return this.grid.selectionSettings.type == 'Multiple';
  }

  // Row object
  private contentModule(): any {
    return this.grid?.contentModule;
  }


  public rowRemove(row: any) {
    var data = this.grid.currentViewData;
    if (data?.length > 0 && row) {
      var removeIndex = data.findIndex((currRow: any) => currRow['id'] === row['id']);
      if (removeIndex >= 0) {
        data.splice(removeIndex, 1); // remove data based on selected index
        this.command.cmdDispatchCommand(UiActionTypeEnum.Redraw);
      }
    }
  }
  public rowsObjects(): any[] {
    return this.contentModule()?.getRows();
  }

  public rowObjectById(id: any): any {
    var pkName = this.grid?.getPrimaryKeyFieldNames()[0];
    return (pkName?.length > 0) ? this.rowsObjects().filter((row: any) => row.isDataRow == true && row.data[pkName] === id)[0]
      : null;
  }

  private rowObjectByElementId(rowElementId: any): any {
    return this.grid.getRowObjectFromUID(rowElementId);
  }

  private rowObjectByElement(rowElement: Element): any {
    return this.rowObjectByElementId(rowElement?.getAttribute('data-uid'));
  }

  // Row data
  public rowDataById(id: any): any {
    return this.rowObjectById(id)?.data;
  }

  public rowDataSetById(id: any, rowData: any) {
    var rowElement: Element = this.rowElementById(id);
    if (rowElement) {
      var stateList: any[] = this.rowElementState(rowElement);
    }
    this.grid.setRowData(id, rowData);
    if (rowElement) {
      this.rowElementStateSet(rowElement, stateList);
    }
  }

  public static rowDataClone(data: any): any {
    return Object.assign({}, data);
  }

  public static rowDataIsEqual(dataRow1: any, dataRow2: any): boolean {
    var dataRow1Json = JSON.stringify(dataRow1);
    var dataRow2Json = JSON.stringify(dataRow2);
    if (dataRow1Json === dataRow2Json) {
      return true;
    }
    return false;
  }

  public static rowsDataEqual(dataRows1: any[], dataRows2: any[]): boolean {
    if (dataRows1?.length != dataRows2?.length) {
      return false;
    }
    for (var i = 0; i < dataRows1.length; i++) {
      if (!CerGridSelectionService.rowDataIsEqual(dataRows1[i], dataRows2[i])) {
        return false;
      }
    }
    return true;
  }

  // Row element
  private gridElement(): HTMLElement {
    return this.grid?.element;
  }

  private rowElementByElementUniqueId(elementUniqueId: any): Element {
    if (elementUniqueId) {
      return this.gridElement().querySelector('[data-uid=' + elementUniqueId + ']');
    }
    return null;
  }

  public rowElementById(id: any): Element {
    var rowObject: any = this.rowObjectById(id);
    var rowElement: Element = this.rowElementByElementUniqueId(rowObject?.uid);
    return rowElement;
  }


  private rowElementState(rowElement: Element): any[] {
    var stateList: any[] = [];
    var cells: NodeListOf<Element> = rowElement?.querySelectorAll('.e-rowcell');
    for (var i = 0, len = cells.length; i < len; i++) {
      if (cells[i] instanceof HTMLElement) {
        var cell: HTMLElement = cells[i] as HTMLElement;
        var state: any = {};
        state.classList = [].slice.call(cell.classList);

        if (cell == document.activeElement) {
          state.focus = true;
        }
        stateList.push(state);
      }
    }
    return stateList;
  }

  // Restore state of classes + focus after row data update
  private rowElementStateSet(rowElement: Element, stateList: any[]) {
    if (rowElement && stateList) {
      var j: number = 0;
      var cells: NodeListOf<Element> = rowElement?.querySelectorAll('.e-rowcell');
      for (var i = 0, len = cells?.length; i < len; i++) {
        if (j < stateList.length && cells[i] instanceof HTMLElement) {
          var cell: HTMLElement = cells[i] as HTMLElement;
          var cellClassList: string[] = [].slice.call(cell.classList);
          var state: any = stateList[j]; j++;
          // classList example: 'e-rowcell e-focus e-focused e-selectionbackground e-active'
          if (state.classList) {
            if (state.classList.includes('e-focus') && !cellClassList.includes('e-focus')) {
              state.classList.splice(state.classList.indexOf('e-focus'), 1);
            }
            if (state.classList.includes('e-focused') && !cellClassList.includes('e-focused')) {
              state.classList.splice(state.classList.indexOf('e-focused'), 1);
            }
            state.classList.forEach((c: string) => { if (!cellClassList.includes(c)) { cell.classList.add(c) } });
            cellClassList.forEach((c: string) => { if (!state.classList.includes(c)) { cell.classList.remove(c) } });
          }
          if (state.focus && cell != document.activeElement) {
            if (!document.activeElement?.classList.contains('e-rowcell')) {
              cell.focus();
            }
          }
        }
      }
    }
  }


  // Clipboard
  private onContextMenuOpen(event: UiCommandEvent) {
    const menuItems: MenuItem[] = event.args.items as MenuItem[];
    //const selectionModeRow = this.copyFieldContextMenuItemVisible(event);
    const copyFieldVisible = this.copyFieldContextMenuItemVisible(event);

    menuItems.forEach(i => {
      if (i.id == uiCmdClipboardCopyField.id) {
        this.command.contextMenuVisible(i, copyFieldVisible);
      }
    });
  }

  private copyFieldContextMenuItemVisible(event: UiCommandEvent): boolean {
    return event.columnName != null && event.rowData != null && event.rowData[event.columnName] != null;
  }

  private doCopySelectedField(event: UiCommandEvent) {
    if (!this.copyFieldContextMenuItemVisible(event)) {
      this.grid.copy();
      return;
    }
    var values: any[] = [];
    if (event.columnName?.length > 0 && event.rowData) {
      var col = this.grid.getColumnByField(event.columnName);
      if (col) {
        var value: any = CerGridFilterService.resolveFieldValue(col, event.rowData[event.columnName]);
        if (value != null) {
          values.push(value.toString());
        }
      }
    }
    if (values?.length > 0) {
      this.copyToClipboard(values);
    }
  }

  public copyToClipboard(data: Object[] = null) {
    var c: any = this.grid.clipboardModule;
    if (c && c.clipBoardTextArea && document.queryCommandSupported('copy') && data?.length > 0 /*&& window.getSelection().toString() === ''*/) {
      c.clipBoardTextArea.value = c.copyContent = '';
      c.getCopyData(data, false, '\t', false);
      var args = {
        data: c.copyContent,
        cancel: false
      };
      this.grid.trigger('beforeCopy', args);
      if (args.cancel) {
        return;
      }
      c.clipBoardTextArea.value = c.copyContent = args.data;
      if (!Browser.userAgent.match(/ipad|ipod|iphone/i)) {
        c.clipBoardTextArea.select();
      }
      else {
        c.clipBoardTextArea.setSelectionRange(0, c.clipBoardTextArea.value.length);
      }
      c.isSelect = true;
      document.execCommand('copy');
      c.clipBoardTextArea.blur();
      window.getSelection().removeAllRanges();
      c.isSelect = false;
    }
  }
}
